import {
  BarChart3,
  Globe,
  Inbox as InboxIcon,
  Mail,
  Megaphone,
  MessageSquare,
  Phone,
  Settings,
} from 'lucide-react';
import type { ComponentType, CSSProperties } from 'react';
import type { Inbox } from '../api/types';
import {
  GmailIcon,
  InstagramIcon,
  MessengerIcon,
  OutlookIcon,
  RingCentralIcon,
  TelegramIcon,
  TwilioIcon,
  WhatsAppIcon,
} from './brandIcons';

/**
 * The Communications module's channel vocabulary, mirroring the design canvas's
 * CH map. Design keys are PRESENTATION groups, not backend channel types —
 * RingCentral and Twilio-SMS both render as "SMS", Twilio-WhatsApp renders as
 * "WhatsApp". Backend channel types stay authoritative for behaviour; these
 * keys only drive tabs, colors and icons.
 */

export type ChannelKey =
  | 'all'
  | 'email'
  | 'whatsapp'
  | 'sms'
  | 'calls'
  | 'telegram'
  | 'facebook'
  | 'instagram'
  | 'website'
  | 'campaigns'
  | 'analytics'
  | 'settings';

/** Lucide components and the brand marks share this shape. */
export type ChannelIcon = ComponentType<{ size?: number | string; style?: CSSProperties }>;

export interface ChannelMeta {
  key: ChannelKey;
  label: string;
  icon: ChannelIcon;
  /** CSS color for the glyph / active tab text. */
  color: string;
  /** CSS background for chips / active tab fill. */
  muted: string;
}

export const CHANNEL_META: Record<ChannelKey, ChannelMeta> = {
  all: {
    key: 'all',
    label: 'All',
    icon: InboxIcon,
    color: 'var(--c-primary)',
    muted: 'var(--c-primary-muted)',
  },
  email: {
    key: 'email',
    label: 'Email',
    icon: Mail,
    color: 'var(--c-blue)',
    muted: 'var(--c-blue-muted)',
  },
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsAppIcon,
    color: 'var(--c-green)',
    muted: 'var(--c-green-muted)',
  },
  sms: {
    key: 'sms',
    label: 'SMS',
    icon: MessageSquare,
    color: 'var(--c-primary)',
    muted: 'var(--c-primary-muted)',
  },
  calls: {
    key: 'calls',
    label: 'Calls',
    icon: Phone,
    color: 'var(--c-orange)',
    muted: 'var(--c-orange-muted)',
  },
  telegram: {
    key: 'telegram',
    label: 'Telegram',
    icon: TelegramIcon,
    color: 'var(--c-lightblue)',
    muted: 'var(--c-blue-muted)',
  },
  // Meta's two: Messenger takes the blue the brand mark carries anyway, and
  // Instagram the one warm hue no other channel has claimed.
  facebook: {
    key: 'facebook',
    label: 'Messenger',
    icon: MessengerIcon,
    color: 'var(--c-blue)',
    muted: 'var(--c-blue-muted)',
  },
  instagram: {
    key: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon,
    color: 'var(--c-purple)',
    muted: 'var(--c-primary-muted)',
  },
  website: {
    key: 'website',
    label: 'Website',
    icon: Globe,
    color: 'var(--c-primary)',
    muted: 'var(--c-primary-muted)',
  },
  campaigns: {
    key: 'campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    color: 'var(--c-blue)',
    muted: 'var(--c-blue-muted)',
  },
  // Not a channel at all: the website-analytics dashboard, offered only inside
  // the dashboard embed (the standalone console has it as a top-level section).
  // `--c-deepblue` is the one categorical hue in this palette no channel has
  // claimed — and note it is NOT what the charts inside use, which are their
  // own validated set; see screens/analytics/analytics.css.
  analytics: {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    color: 'var(--c-deepblue)',
    muted: 'var(--c-blue-muted)',
  },
  // The tab holds more than channels — team access and notifications live
  // there too — so it is labelled for the job, not the contents. The KEY and the
  // LABEL now agree: the dashboard deep-links `?tab=settings` and always did, and
  // "Manage" collided with the per-channel Manage button one screen down, where
  // the same word meant one channel's credentials rather than the whole module's
  // settings.
  settings: {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    color: 'var(--c-muted)',
    muted: 'var(--c-secondary)',
  },
};

/**
 * How tall the reply box may grow before it starts scrolling, in px.
 *
 * The composer opens one line high and grows with what is typed, so this is the
 * only thing deciding how much of a message an agent sees at once — and that
 * differs by channel because the messages do. A mail is a letter and gets the
 * room; an SMS long enough to need it is already several segments, and a box
 * that stops growing says so before the bill does.
 *
 * Keyed on the presentation group rather than the channel type, so a WhatsApp
 * sender hosted on Twilio is sized as WhatsApp and not as an SMS line.
 */
export const COMPOSER_MAX_HEIGHT: Partial<Record<ChannelKey, number>> = {
  email: 320,
  whatsapp: 200,
  telegram: 200,
  facebook: 200,
  instagram: 200,
  website: 200,
  sms: 128,
};

/**
 * What a key with no entry of its own gets — and what an INTERNAL NOTE gets on
 * every channel: a note is never delivered, so the channel's limits have
 * nothing to say about how long one may be.
 */
export const COMPOSER_MAX_HEIGHT_DEFAULT = 200;

/** Conversation-bearing keys, in the design's tab order. */
export const CONVERSATION_KEYS: ChannelKey[] = [
  'email',
  'whatsapp',
  'sms',
  'facebook',
  'instagram',
  'telegram',
  'website',
];

/** Which presentation group an inbox belongs to. */
export function channelKeyForInbox(inbox: Inbox): ChannelKey {
  switch (inbox.channelType) {
    case 'email':
      return 'email';
    case 'ringcentral':
      return 'sms';
    case 'whatsapp':
      return 'whatsapp';
    case 'telegram':
      return 'telegram';
    case 'twilio':
      return inbox.channel.medium === 'whatsapp' ? 'whatsapp' : 'sms';
    case 'website':
      return 'website';
    case 'facebook':
      return 'facebook';
    case 'instagram':
      return 'instagram';
  }
}

export const channelMetaForInbox = (inbox: Inbox): ChannelMeta =>
  CHANNEL_META[channelKeyForInbox(inbox)];

/**
 * The PER-INBOX mark — the provider's real brand where one exists (the design
 * uses brand marks in account rails, channel dots and settings cards), the
 * channel-group glyph otherwise. A keyed record (not a component-returning
 * function) so render-time lookups are plain property access.
 */
export const INBOX_ICONS: Record<string, ChannelIcon> = {
  gmail: GmailIcon,
  outlook: OutlookIcon,
  mail: Mail,
  whatsapp: WhatsAppIcon,
  telegram: TelegramIcon,
  twilio: TwilioIcon,
  ringcentral: RingCentralIcon,
  sms: MessageSquare,
  website: Globe,
  facebook: MessengerIcon,
  instagram: InstagramIcon,
};

export function inboxIconKey(inbox: Inbox): keyof typeof INBOX_ICONS {
  switch (inbox.channelType) {
    case 'email':
      if (inbox.channel.provider === 'google') return 'gmail';
      if (inbox.channel.provider === 'microsoft') return 'outlook';
      return 'mail';
    case 'whatsapp':
      return 'whatsapp';
    case 'telegram':
      return 'telegram';
    case 'twilio':
      return inbox.channel.medium === 'whatsapp' ? 'whatsapp' : 'twilio';
    case 'ringcentral':
      return 'ringcentral';
    case 'website':
      return 'website';
    case 'facebook':
      return 'facebook';
    case 'instagram':
      return 'instagram';
  }
}

/**
 * The same mark for a caller holding only a channel TYPE — a notification
 * snapshot carries the type, never the inbox row, and even a caller that could
 * look one up is left with nothing by a deleted or not-yet-loaded inbox.
 *
 * Deliberately the coarser answer: `email` cannot become Gmail without the
 * provider, and a Twilio-hosted WhatsApp sender stays Twilio. Both match how
 * the SERVER names those channels ("Email", "Twilio"), so the mark and the
 * words beside it never disagree.
 *
 * `Partial` because the key is a wire string and not every one has a mark —
 * unlike INBOX_ICONS above, which is only ever indexed by inboxIconKey().
 */
export const CHANNEL_TYPE_ICONS: Partial<Record<string, ChannelIcon>> = {
  email: Mail,
  whatsapp: WhatsAppIcon,
  telegram: TelegramIcon,
  twilio: TwilioIcon,
  ringcentral: RingCentralIcon,
  website: Globe,
  facebook: MessengerIcon,
  instagram: InstagramIcon,
};

/** The presentation group a bare channel type belongs to — see CHANNEL_TYPE_ICONS. */
export const CHANNEL_TYPE_META: Partial<Record<string, ChannelMeta>> = {
  email: CHANNEL_META.email,
  whatsapp: CHANNEL_META.whatsapp,
  telegram: CHANNEL_META.telegram,
  twilio: CHANNEL_META.sms,
  ringcentral: CHANNEL_META.sms,
  website: CHANNEL_META.website,
  facebook: CHANNEL_META.facebook,
  instagram: CHANNEL_META.instagram,
};

/**
 * Can this inbox START a conversation to this contact? (Reply capability is the
 * server's call — this only gates the New-message dialog.) Telegram bots cannot
 * initiate chats, so telegram is never composable.
 */
export function inboxCanReach(
  inbox: Inbox,
  contact: { email: string | null; phoneNumber: string | null },
): boolean {
  switch (inbox.channelType) {
    case 'email':
      return contact.email !== null && contact.email !== '';
    case 'ringcentral':
    case 'whatsapp':
      return contact.phoneNumber !== null && contact.phoneNumber !== '';
    case 'twilio':
      return contact.phoneNumber !== null && contact.phoneNumber !== '';
    case 'telegram':
      return false;
    case 'website':
      // A visitor session only exists once THEY write in — agents cannot ring
      // an anonymous browser.
      return false;
    case 'facebook':
    case 'instagram':
      // Page-scoped ids exist only once the person has written to the Page or
      // account; there is nothing to address a first message to.
      return false;
  }
}

/** True when the tenant has any inbox that can carry voice (the Calls tab). */
export function hasVoiceInbox(inboxes: Inbox[]): boolean {
  return inboxes.some(
    (inbox) =>
      inbox.channelType === 'ringcentral' ||
      (inbox.channelType === 'twilio' && inbox.channel.voiceEnabled),
  );
}
